<script setup>
import { useUsersStore, useTagsStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { sanitizeHtml } from '@core/utils/sanitizeHtml'
import MemberVerifiedTags from '@core/components/MemberVerifiedTags.vue'
import { buildProfileUrl, getFullUrl, displayPhone } from '@/plugins/misc'
import {
  phoneValidator,
  urlValidator,
  nameValidator,
  linkedInProfileValidator,
  instagramProfileValidator,
  tiktokProfileValidator,
  xProfileValidator
} from '@validators'
import { computed } from 'vue'
import rmLogo from '@/assets/logo.svg'
import { isRestrictedSimulationScope } from '@/utils/simulationScopes'

const emit = defineEmits([
  'submit',
])

const usersStore = useUsersStore()
const tagsStore = useTagsStore()
let isEditingMyUser  = null
let myUser = null;
({ myUser, isEditingMyUser } = storeToRefs(usersStore))

// RM-150 / RM-159 / RM-319: a RESTRICTED simulation (a personal admin, spouse or
// group admin acting for a member) must not delete the member's account or see the
// spouse-edit audit; the backend enforces the same via RESTRICTED_SIMULATION_SCOPES
// (see routes/api/user.js). A platform (RM) admin simulation is fully privileged,
// so it sees the member's complete view — these controls are gated on the
// restricted scopes only (utils/simulationScopes), not on simulation in general.
const isSimulation = computed(() => !!localStorage.getItem('simulation'))
const isRestrictedSimulation = computed(
  () => isSimulation.value && isRestrictedSimulationScope(localStorage.getItem('simulationScope')),
)

// RM-159: unreverted profile edits made by a Tier 2 spouse, shown to the
// account holder on their own profile so they can review and revert them.
const spouseEdits = computed(() => usersStore.spouseEdits || [])
const spouseName = computed(() => {
  const linked = myUser.value?.isSecondaryAccount ? myUser.value?.primaryAccount : myUser.value?.secondaryAccount
  const name = [linked?.firstName, linked?.lastName].filter(Boolean).join(' ')

  return name || 'your spouse'
})

const profileFieldLabels = {
  firstName: 'First name',
  lastName: 'Last name',
  phoneNumber: 'Mobile phone',
  company: 'Company',
  title: 'Position/Title',
  company2: 'Company 2',
  title2: 'Position/Title 2',
  bio: 'Bio',
  profileVisibility: 'Privacy/Visibility',
  profileWebsiteLink: 'Bio page',
  profileLinkedInProfileId: 'LinkedIn',
  profileInstagramProfileId: 'Instagram',
  profileXProfileId: 'X',
  profileTikTokProfileId: 'TikTok',
  profileImageId: 'Profile picture',
}
const fieldLabel = field => profileFieldLabels[field] || field
const displayValue = value => {
  if (value === null || value === undefined || value === '') return '(empty)'

  return String(value)
}

const revertingLogId = ref(null)
const revertSpouseEdit = async logId => {
  revertingLogId.value = logId
  try {
    await usersStore.revertSpouseEdit(logId)
  } finally {
    revertingLogId.value = null
  }
}

onMounted(() => {
  tagsStore.getAvailableTags()
})

watch(isEditingMyUser, opened => {
  if (opened && !isRestrictedSimulation.value) usersStore.fetchSpouseEdits()

  // RM-430: opened from Profile → RM via text to add a mobile: land on that field
  if (opened && usersStore.editMyUserFocus === 'phoneNumber') {
    usersStore.editMyUserFocus = null
    setTimeout(() => {
      const field = document.getElementById('edit-profile-mobile')
      if (field) {
        field.scrollIntoView({ block: 'center' })
        field.focus()
      }
    }, 300)
  }
}, { immediate: true })

const choosingProfileImage = ref(false)

// Form Errors
const errors = ref({})

const onFormSubmit = async () => {
  if(valid.value){
    await usersStore.showLoadingOverlay()
    let saveErrors = await usersStore.updateMyUser()
    if(saveErrors){
      errors.value = Object.assign(errors.value, saveErrors)
    }
    else{
      changesMade.value = false
    }
    await usersStore.hideLoadingOverlay()
  } else{
    //form is not valid!
  }
}

const incompleteForm = !myUser.value.firstName || !myUser.value.lastName || !myUser.value.title || !myUser.value.company || !myUser.value.phoneNumber

const form = ref()
const valid = ref(true)
const isConfirmDialogOpen = ref(false)
const isDeleteAccountDialogOpen = ref(false)
const isProfileSummaryDialogOpen = ref(false)
const changesMade = ref(false)
const currentTab = ref('personal')

const requestClose = () => {
  // isConfirmDialogOpen.value = true
  if (changesMade.value) {
    isConfirmDialogOpen.value = true
  }
  else{
    usersStore.cancelEditUseMyUser()
  }
}
const handleMediaSelection = selectedMedia => {
  // Do something with the selected media
  console.log(selectedMedia)
  changesMade.value = true
  if(selectedMedia){
    //rips out any ?t=9287439832749 that may have been added to refresh the image after cropping
    myUser.value.profileImageId = selectedMedia.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)[0]
    choosingProfileImage.value = false
  } else{
    myUser.value.profileImageId = null

    //choosingProfileImage.value = false
  }
}
const cancelDeleteAccount = async () => {
  myUser.value.markedForDeletion = false
  await usersStore.updateMyUser()
}

// Optional validators - only validate if value is provided
const optionalLinkedInValidator = value => {
  if (!value || value.trim() === '') return true
  
  return linkedInProfileValidator(value)
}

const optionalInstagramValidator = value => {
  if (!value || value.trim() === '') return true
  
  return instagramProfileValidator(value)
}

const optionalXValidator = value => {
  if (!value || value.trim() === '') return true
  
  return xProfileValidator(value)
}

const optionalTiktokValidator = value => {
  if (!value || value.trim() === '') return true
  
  return tiktokProfileValidator(value)
}

const optionalUrlValidator = value => {
  if (!value || value.trim() === '') return true
  
  return urlValidator(value)
}

// Computed properties to normalize social media handles
// Extracts username/handle from full URLs or returns the raw value
const linkedInHandle = computed({
  get: () => {
    if (!myUser.value.profileLinkedInProfileId) return ''
    const value = myUser.value.profileLinkedInProfileId

    // Extract username from various LinkedIn URL formats
    const match = value.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([^\/\?]+)/)
    
    return match ? match[1] : value
  },
  set: value => {
    myUser.value.profileLinkedInProfileId = value
    changesMade.value = true
  }
})

const instagramHandle = computed({
  get: () => {
    if (!myUser.value.profileInstagramProfileId) return ''
    const value = myUser.value.profileInstagramProfileId

    // Extract username from various Instagram URL formats or @handle
    const match = value.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^\/\?]+)/)
    if (match) return match[1]

    // Remove @ if present
    return value.replace(/^@/, '')
  },
  set: value => {
    // Store without @ prefix
    myUser.value.profileInstagramProfileId = value.replace(/^@/, '')
    changesMade.value = true
  }
})

const xHandle = computed({
  get: () => {
    if (!myUser.value.profileXProfileId) return ''
    const value = myUser.value.profileXProfileId

    // Extract username from various X/Twitter URL formats or @handle
    const match = value.match(/(?:https?:\/\/)?(?:www\.)?(?:x\.com|twitter\.com)\/([^\/\?]+)/)
    if (match) return match[1]

    // Remove @ if present
    return value.replace(/^@/, '')
  },
  set: value => {
    // Store without @ prefix
    myUser.value.profileXProfileId = value.replace(/^@/, '')
    changesMade.value = true
  }
})

const tiktokHandle = computed({
  get: () => {
    if (!myUser.value.profileTikTokProfileId) return ''
    const value = myUser.value.profileTikTokProfileId

    // Extract username from various TikTok URL formats or @handle
    const match = value.match(/(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@?([^\/\?]+)/)
    if (match) return match[1]

    // Remove @ if present
    return value.replace(/^@/, '')
  },
  set: value => {
    // Store without @ prefix
    myUser.value.profileTikTokProfileId = value.replace(/^@/, '')
    changesMade.value = true
  }
})

const filteredAvailableTags = computed(() => {
  const verifiedIds = (myUser.value.verifiedTags || []).map(t => (t._id || t).toString())
  const requestedIds = (myUser.value.requestedTags || []).map(t => (t._id || t).toString())
  const excludeIds = [...verifiedIds, ...requestedIds]
  
  return (tagsStore.availableTags || []).filter(t => !excludeIds.includes(t._id.toString())).sort((a, b) => (a.tagName || '').localeCompare(b.tagName || ''))
})

const selectedNewTags = ref([])

const addRequestedTags = async () => {
  if (selectedNewTags.value.length > 0) {
    if (!myUser.value.requestedTags) myUser.value.requestedTags = []
    for (const tag of selectedNewTags.value) {
      if (typeof tag === 'string') {
        const trimmed = tag.trim()
        if (!trimmed) continue
        const result = await tagsStore.requestCustomTag(trimmed)
        if (result) {
          myUser.value.requestedTags.push(result)
        }
      } else {
        myUser.value.requestedTags.push(tag)
      }
    }
    selectedNewTags.value = []
    changesMade.value = true
  }
}

const removeRequestedTag = tag => {
  if (!myUser.value.requestedTags) return
  const tagId = (tag._id || tag).toString()
  myUser.value.requestedTags = myUser.value.requestedTags.filter(
    t => (t._id || t).toString() !== tagId
  )
  changesMade.value = true
}

const profileUrlCopied = ref(false)
const copyProfileUrl = async () => {
  if (!myUser.value.profileId) return
  const fullUrl = `reserverm.com/m/${myUser.value.profileId}`
  try {
    await navigator.clipboard.writeText(fullUrl)
    profileUrlCopied.value = true
    setTimeout(() => {
      profileUrlCopied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy URL:', err)
  }
}

const showSecondPosition = ref(!!(myUser.value.company2 || myUser.value.title2))

const profileSummaryRef = ref(null)
const copyProfileSuccess = ref(false)
const copyProfileSummary = async () => {
  const el = profileSummaryRef.value
  if (!el) return

  const html = el.innerHTML
  const text = el.innerText

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' })
      })
    ])
    copyProfileSuccess.value = true
    setTimeout(() => { copyProfileSuccess.value = false }, 2000)
  } catch (err) {
    try {
      await navigator.clipboard.writeText(text)
      copyProfileSuccess.value = true
      setTimeout(() => { copyProfileSuccess.value = false }, 2000)
    } catch (e) {
      console.error('Copy failed:', e)
    }
  }
}
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmDialogOpen"
    confirmation-msg="Save changes?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="onFormSubmit(); changesMade = false;"
    @cancel="usersStore.cancelEditUseMyUser(); changesMade = false;"
  />
  <DeleteAccountDialog
    v-model:is-dialog-visible="isDeleteAccountDialogOpen"
  />
  <VDialog
    :model-value="isEditingMyUser"
    persistent
    fullscreen
    :retain-focus="false"
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VSpacer />
      <VToolbarTitle>Profile</VToolbarTitle>
      <VSpacer />
    </VToolbar>
    <VCard
      class="pa-sm-9 pa-2 main-content"
      @keyup="changesMade = true"
    >
      <div class="text-center">
        <div v-if="(!myUser.firstName || !myUser.lastName) && myUser?.primaryGroupPermission == 'pending'">
          Please complete your profile<br>needs to be completed for {{ myUser?.primaryGroupName }} to approve your Platinum Membership
        </div>
        <div
          v-else-if="myUser?.markedForDeletion"
          class=" mb-6"
        >
          <div class="mt-4 text-center text-error">
            Account Deletion in Progress
          </div><VBtn
            class="mt-1"
            @click="cancelDeleteAccount"
          >
            Cancel account deletion
          </VBtn>
        </div>
        <div
          v-else-if="incompleteForm"
          class="text-lg mt-4 px-2"
        >
          Your profile helps you get reservations
        </div>
      </div>
      <VCardText>
        <div class="flex flex-column align-center">
          <div
            class="avatar rounded-circle mb-2"
            @click="choosingProfileImage = !choosingProfileImage"
          >
            <VImg
              v-if="myUser.profileImageId"
              class="avatar-image"
              :src="mediaUrl(myUser.profileImageId, 'small')"
              cover
            />
            <div
              v-else-if="myUser?.firstName?.length > 0 || myUser?.lastName?.length > 0"
              class="avatar-fallback flex flex-row align-center justify-center"
            >
              <span class="avatar-fallback-text">{{ myUser.firstName?.[0]?.toUpperCase() }}{{ myUser.lastName?.[0]?.toUpperCase() }}</span>
            </div>

            <div
              v-else
              class="avatar-fallback flex flex-row align-center justify-center"
            >
              <span class="avatar-fallback-text text-xl"><VIcon>mdi-user</VIcon></span>
            </div>

            <div class="avatar-camera-icon-holder">
              <VIcon class="avatar-camera-icon">
                mdi-camera
              </VIcon>
            </div>
          </div>
          <div
            v-if="!myUser.profileImageId"
            class="text-secondary mt-1 text-caption text-center mb-3"
          >
            Add Profile Picture
          </div>
          <div
            class="text-primary text-caption cursor-pointer mb-3"
            @click="isProfileSummaryDialogOpen = true"
          >
            View Profile Summary
          </div>
        </div>
        <!-- Profile Summary Dialog -->
        <VDialog
          v-model="isProfileSummaryDialogOpen"
          max-width="520"
        >
          <VCard class="profile-summary-dialog">
            <div class="profile-summary-dialog-header">
              <div class="profile-summary-dialog-title text-center">
                Shared with your reservation requests
              </div>
              <VBtn
                icon="mdi-close"
                variant="text"
                size="small"
                @click="isProfileSummaryDialogOpen = false"
              />
            </div>
            <VDivider />
            <VCardText class="pa-6">
              <!-- Avatar + Name (not included in copy) -->
              <div class="d-flex align-center mb-4">
                <div class="profile-summary-avatar rounded-circle mr-3">
                  <VImg
                    v-if="myUser.profileImageId"
                    class="profile-summary-avatar-img"
                    :src="mediaUrl(myUser.profileImageId, 'small')"
                    cover
                  />
                  <div
                    v-else
                    class="profile-summary-avatar-fallback d-flex align-center justify-center"
                  >
                    <span>{{ myUser.firstName?.[0]?.toUpperCase() }}{{ myUser.lastName?.[0]?.toUpperCase() }}</span>
                  </div>
                </div>
                <div class="profile-summary-name">
                  {{ myUser.firstName }} {{ myUser.lastName }}
                </div>
              </div>
              <div
                ref="profileSummaryRef"
                class="profile-summary-content"
              >
                <div>
                  <strong>{{ myUser.firstName }} {{ myUser.lastName }} - RM Profile Details</strong>
                </div>
                <div
                  v-if="myUser.membershipType"
                  class="profile-summary-line"
                >
                  ({{ myUser.verified ? 'Verified + ' : '' }}{{ myUser.membershipType }} member)
                </div>
                <div
                  v-if="myUser.phoneNumber"
                  class="profile-summary-line"
                >
                  <strong>M.</strong> {{ displayPhone(myUser.phoneNumber) }}
                </div>
                <div
                  v-if="myUser.email"
                  class="profile-summary-line"
                >
                  <strong>E.</strong> {{ myUser.email }}
                </div>
                <div
                  v-if="myUser.title || myUser.company"
                  class="profile-summary-line"
                >
                  <template v-if="myUser.title && myUser.company">
                    {{ myUser.title }}, {{ myUser.company }}
                  </template>
                  <template v-else>
                    {{ myUser.title || myUser.company }}
                  </template>
                </div>
                <div
                  v-if="myUser.title2 || myUser.company2"
                  class="profile-summary-line"
                >
                  <template v-if="myUser.title2 && myUser.company2">
                    {{ myUser.title2 }}, {{ myUser.company2 }}
                  </template>
                  <template v-else>
                    {{ myUser.title2 || myUser.company2 }}
                  </template>
                </div>
                <div
                  v-if="myUser.bio"
                  class="profile-summary-line mt-3"
                >
                  <strong>Bio:</strong> {{ myUser.bio }}
                </div>
                <div
                  v-if="myUser.verifiedTags?.length > 0"
                  class="profile-summary-tags mt-3"
                >
                  <div
                    v-for="tag in myUser.verifiedTags"
                    :key="tag._id || tag.tagName"
                    class="profile-summary-tag-line"
                  >
                    <strong>{{ tag.tagName }}:</strong> <span v-html="sanitizeHtml(tag.tagShortDescription || tag.tagDescription || 'Verified by RM')" />
                  </div>
                </div>
                <div
                  v-if="myUser.profileLinkedInProfileId || myUser.profileInstagramProfileId || myUser.profileXProfileId || myUser.profileTikTokProfileId || myUser.profileWebsiteLink || myUser.profileId"
                  class="profile-summary-links mt-3"
                >
                  <div
                    v-if="myUser.profileLinkedInProfileId"
                    class="profile-summary-link-line"
                  >
                    <strong>LinkedIn: </strong>
                    <a
                      :href="buildProfileUrl('linkedin', myUser.profileLinkedInProfileId)"
                      target="_blank"
                      @click.stop=""
                    >linkedin.com/in/{{ myUser.profileLinkedInProfileId }}</a>
                  </div>
                  <div
                    v-if="myUser.profileInstagramProfileId"
                    class="profile-summary-link-line"
                  >
                    <strong>Instagram:</strong> <a
                      :href="buildProfileUrl('instagram', myUser.profileInstagramProfileId)"
                      target="_blank"
                      @click.stop=""
                    >instagram.com/{{ myUser.profileInstagramProfileId }}</a>
                  </div>
                  <div
                    v-if="myUser.profileXProfileId"
                    class="profile-summary-link-line"
                  >
                    <strong>X:</strong> <a
                      :href="buildProfileUrl('twitter', myUser.profileXProfileId)"
                      target="_blank"
                      @click.stop=""
                    >x.com/{{ myUser.profileXProfileId }}</a>
                  </div>
                  <div
                    v-if="myUser.profileTikTokProfileId"
                    class="profile-summary-link-line"
                  >
                    <strong>TikTok:</strong> <a
                      :href="buildProfileUrl('tiktok', myUser.profileTikTokProfileId)"
                      target="_blank"
                      @click.stop=""
                    >tiktok.com/@{{ myUser.profileTikTokProfileId }}</a>
                  </div>
                  <div
                    v-if="myUser.profileWebsiteLink"
                    class="profile-summary-link-line"
                  >
                    <strong>Bio Page: </strong> <a
                      :href="getFullUrl(myUser.profileWebsiteLink)"
                      target="_blank"
                      @click.stop=""
                    >{{ myUser.profileWebsiteLink }}</a>
                  </div>
                  <div
                    v-if="myUser.profileId"
                    class="profile-summary-link-line"
                  >
                    <strong>RM: </strong> <a
                      :href="`/m/${myUser.profileId}?public`"
                      target="_blank"
                      @click.stop=""
                    >reserverm.com/m/{{ myUser.profileId }}</a>
                  </div>
                </div>
              </div>
              <div class="profile-summary-content">
                <div
                  v-if="myUser.profileId"
                  class="profile-summary-link-line"
                >
                  <a
                    :href="`/m/${myUser.profileId}?public`"
                    target="_blank"
                    @click.stop=""
                  >(public view)</a>
                </div>
              </div>
            </VCardText>
            <VDivider />
            <VCardActions class="pa-4">
              <VSpacer />
              <VBtn
                variant="outlined"
                size="small"
                @click="isProfileSummaryDialogOpen = false"
              >
                Close
              </VBtn>
              <VBtn
                :color="copyProfileSuccess ? 'success' : 'primary'"
                :prepend-icon="copyProfileSuccess ? 'mdi-check' : 'mdi-content-copy'"
                size="small"
                @click="copyProfileSummary"
              >
                {{ copyProfileSuccess ? 'Copied!' : 'Copy Profile' }}
              </VBtn>
            </VCardActions>
          </VCard>
        </VDialog>
        <!-- RM-159: spouse-edit review panel (account holder's own view) -->
        <div
          v-if="!isRestrictedSimulation && spouseEdits.length > 0"
          class="spouse-edits-panel mb-6"
        >
          <div class="spouse-edits-title">
            Recent changes by {{ spouseName }}
          </div>
          <div class="spouse-edits-subtitle">
            Your spouse made these edits to your profile. Revert anything you didn't want changed.
          </div>
          <div
            v-for="edit in spouseEdits"
            :key="edit._id"
            class="spouse-edit-row"
          >
            <div class="spouse-edit-changes">
              <div
                v-for="(change, i) in edit.changes"
                :key="i"
                class="spouse-edit-change"
              >
                <span class="spouse-edit-field">{{ fieldLabel(change.field) }}</span>
                <span class="spouse-edit-values">
                  <span class="spouse-edit-old">{{ displayValue(change.oldValue) }}</span>
                  <VIcon
                    size="14"
                    class="mx-1"
                  >mdi-arrow-right</VIcon>
                  <span class="spouse-edit-new">{{ displayValue(change.newValue) }}</span>
                </span>
              </div>
            </div>
            <VBtn
              variant="text"
              color="primary"
              size="small"
              :loading="revertingLogId === edit._id"
              :disabled="revertingLogId !== null"
              @click="revertSpouseEdit(edit._id)"
            >
              Revert
            </VBtn>
          </div>
        </div>

        <!-- 👉 Form -->
        <VForm
          ref="form"
          v-model="valid"
          class=" mb-16 pb-16"
          @submit.prevent="onFormSubmit"
        >
          <MediaChooserAndManager
            v-model:is-dialog-visible="choosingProfileImage"
            title="Choose a Profile Image"
            :preselected-media-ids="[myUser?.profileImageId]"
            @media-selected="handleMediaSelection"
          />
          <VCol>
            <VTabs
              v-model="currentTab"
              class="mb-6"
            >
              <VTab value="personal">
                Details
              </VTab>
              <VTab
                value="affiliations"
              >
                Verified
              </VTab>
              <VTab value="social">
                Links
              </VTab>
            </VTabs>

            <VWindow
              v-model="currentTab"
              transition="fade-transition"
              reverse-transition="fade-transition"
            >
              <VWindowItem value="personal">
                <h4 class="mb-4">
                  Personal details
                </h4>
                <!-- 👉 Full Name -->
                <div class="flex flex-column">
                  <VTextField
                    v-model="myUser.firstName"
                    autocomplete="given-name"
                    label="First Name"
                    :rules="[nameValidator]"
                    required
                  />

                  <VTextField
                    v-model="myUser.lastName"
                    autocomplete="family-name"
                    label="Last Name"
                    :rules="[nameValidator]"
                    required
                  />

                  <VTextField
                    id="edit-profile-mobile"
                    v-model="myUser.phoneNumber"
                    class="mb-1"
                    autocomplete="tel"
                    label="Mobile Phone"
                    :rules="[phoneValidator]"
                    required
                    hint="(required for reservations)"
                    persistent-hint
                    @update:model-value="changesMade = true"
                  />

                  <VTextField
                    v-if="myUser.userLevel != 'dummy'"
                    v-model="myUser.email"
                    class="mb-2 disabled-readable"
                    label="Email"
                    filled
                    disabled
                    hint="(email preferred@reserverm.com to update)"
                    persistent-hint
                  />
                </div>

                <div class="flex flex-column">
                  <VTextField
                    v-model="myUser.company"
                    autocomplete="organization"
                    label="Company"
                    :rules="[]"
                    required
                  />
                  <VTextField
                    v-model="myUser.title"
                    autocomplete="organization-title"
                    label="Position/Title"
                    :rules="[]"
                    required
                  />
                  <template v-if="showSecondPosition">
                    <VTextField
                      v-model="myUser.company2"
                      autocomplete="organization"
                      label="Company 2"
                      :rules="[]"
                      required
                    />
                    <VTextField
                      v-model="myUser.title2"
                      autocomplete="organization-title"
                      label="Position/Title 2"
                      :rules="[]"
                      required
                    />
                  </template>
                  <span
                    v-else
                    class="text-primary text-caption cursor-pointer mb-4"
                    @click="showSecondPosition = true"
                  >
                    + Add another
                  </span>
                  <VTextarea
                    v-model="myUser.bio"
                    class="mb-2"
                    label="Bio"
                    :rules="[]"
                    required
                    counter="500"
                    maxlength="500"
                  />
                  <div class="d-flex align-center">
                    <VTextField
                      v-model="myUser.profileId"
                      class="profile-id-field"
                      label="RM Profile"
                      prefix="reserverm.com/m/"
                      disabled
                      required
                      hide-details
                    />
                  </div>
                  <div
                    v-if="myUser.profileId"
                    class="copy-link text-primary text-caption cursor-pointer ml-2 mb-4"
                    @click="copyProfileUrl"
                  >
                    {{ profileUrlCopied ? 'copied!' : 'copy RM Profile link' }}
                  </div>
                  <VSelect
                    v-model="myUser.profileVisibility"
                    density="compact"
                    label="Privacy/Visibility"
                    item-title="text"
                    item-value="value"
                    :items="[{text:'Public', value: 'public'},{text:'Private', value: 'private'}]"
                    required
                    @update:model-value="changesMade = true"
                  />
                </div>
                <div
                  v-if="!isRestrictedSimulation"
                  class="text-center align-center mt-8"
                >
                  <span
                    v-if="!myUser?.markedForDeletion"
                    class="mt-4 text-center"
                  ><span
                    class="text-primary cursor-pointer"
                    @click="isDeleteAccountDialogOpen = !isDeleteAccountDialogOpen"
                  >delete account</span></span>
                  <span
                    v-else-if="myUser?.markedForDeletion"
                    class="mt-4 text-center"
                  ><span
                    class="text-primary cursor-pointer"
                    @click="cancelDeleteAccount"
                  >cancel account deletion</span></span>
                </div>
              </VWindowItem>

              <VWindowItem
                value="affiliations"
              >
                <h4 class="mb-2">
                  Verified status for members of select clubs/groups &
                  investors or executives/partners of restaurants
                </h4>
                <VDivider
                  v-if="myUser.verifiedTags?.length > 0"
                  class="section-divider my-4"
                />

                <div
                  v-if="myUser.verifiedTags?.length > 0"
                  class="mb-4"
                >
                  <h4 class="mb-2">
                    Your affiliations:
                  </h4>
                  <MemberVerifiedTags :member="myUser" />
                </div>

                <div
                  v-if="myUser.requestedTags?.length > 0"
                  class="mb-4"
                >
                  <h4 class="mb-2">
                    Pending requests:
                  </h4>
                  <div class="d-flex flex-wrap gap-2">
                    <VChip
                      v-for="tag in myUser.requestedTags"
                      :key="(tag._id || tag).toString()"
                      color="warning"
                      variant="tonal"
                      closable
                      @click:close="removeRequestedTag(tag)"
                    >
                      <VIcon
                        start
                        size="small"
                      >
                        mdi-clock-outline
                      </VIcon>
                      {{ tag.tagName || 'Pending' }}
                    </VChip>
                  </div>
                </div>

                <VDivider class="section-divider my-4" />
                <h4 class="mb-2">
                  Request verified status:
                </h4>
                <div class="d-flex align-center gap-2">
                  <VCombobox
                    v-model="selectedNewTags"
                    label="Enter or select affiliation"
                    item-title="tagName"
                    item-value="_id"
                    :items="filteredAvailableTags"
                    chips
                    closable-chips
                    hide-selected
                    multiple
                    return-object
                    class="flex-grow-1"
                  />
                </div>
                <div class="d-flex align-center gap-2 mt-4 align-center text-center justify-center">
                  <VBtn
                    :disabled="selectedNewTags.length === 0"
                    color="primary"
                  
                    @click="addRequestedTags"
                  >
                    Request
                  </VBtn>
                </div>
              </VWindowItem>

              <VWindowItem value="social">
                <h4 class="mb-4">
                  Professional &amp; Social Links
                </h4>
                
                <div class="flex flex-column">
                  <div class="d-flex align-center">
                    <VTextField
                      v-model="myUser.profileId"
                      class="profile-id-field"
                      label="RM Profile"
                      prefix="reserverm.com/m/"
                      disabled
                      required
                      hide-details
                    >
                      <template #prepend-inner>
                        <img
                          :src="rmLogo"
                          alt="RM"
                          class="rm-logo-icon"
                        >
                      </template>
                    </VTextField>
                  </div>
                  <div
                    v-if="myUser.profileId"
                    class="copy-link text-primary text-caption cursor-pointer ml-2 mb-4"
                    @click="copyProfileUrl"
                  >
                    {{ profileUrlCopied ? 'copied!' : 'copy RM Profile link' }}
                  </div>
                  <VTextField
                    v-model="myUser.profileWebsiteLink"
                    label="Bio page"
                    type="string"
                    prepend-inner-icon="mdi-web"
                    hint="enter full url to company/personal website bio"
                    :rules="[optionalUrlValidator]"
                  />
                  <div class="d-flex align-center">
                    <VTextField
                      v-model="linkedInHandle"
                      label="LinkedIn"
                      type="string"
                      prepend-inner-icon="mdi-linkedin"
                      :hint="linkedInHandle ? `linkedin.com/in/${linkedInHandle}` : 'enter profile id'"
                      persistent-hint
                      :rules="[optionalLinkedInValidator]"
                    />
                  </div>
                  <VTextField
                    v-model="instagramHandle"
                    label="Instagram"
                    type="string"
                    prepend-inner-icon="mdi-instagram"
                    hint="enter username"
                    prefix="@"
                    :rules="[optionalInstagramValidator]"
                  />
                  <VTextField
                    v-model="xHandle"
                    label="X"
                    type="string"
                    prepend-inner-icon="simple-icons:x"
                    hint="enter handle/username"
                    prefix="@"
                    :rules="[optionalXValidator]"
                  />
                  <VTextField
                    v-model="tiktokHandle"
                    label="TikTok"
                    type="string"
                    prepend-inner-icon="simple-icons:tiktok"
                    hint="enter username"
                    prefix="@"
                    :rules="[optionalTiktokValidator]"
                  />
                </div>
              </VWindowItem>
            </VWindow>


            <!-- 👉 Submit and Cancel -->
            <div
              v-if="(!myUser.firstName || !myUser.lastName) && myUser?.primaryGroupPermission == 'pending'"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                type="submit"
                :disabled="!myUser.firstName || !myUser.lastName"
                @click="form?.validate(); myUser.submitForApproval = true; "
              >
                Submit for Approval
              </VBtn>
            </div>
            <div
              v-else
              class="action-buttons d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                :disabled="valid == null || valid == false || !changesMade"
                @click="form?.validate(); onFormSubmit()"
              >
                Save
              </VBtn>

              <VBtn
                color="secondary"
                variant="tonal"
                @click="requestClose"
              >
                Cancel
              </VBtn>
            </div>
          </VCol>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.v-toolbar-title{
  flex: unset;
  margin-inline-start:unset;
}

.toolbar-header{
  position: fixed;
  z-index: 1000;
}

.main-content{
  top: 60px;
  position: relative;
  padding-bottom: 80px !important;
}

.avatar {
  cursor: pointer;
  position: relative;
  width: 6rem;
  height: 6rem;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.25rem;
}

@media screen and (min-width: 670px) {
  .avatar {
    width: 6rem;
    height: 6rem;
    outline-offset: 0.5rem;
    margin: 1rem 2rem 0;
  }

  .avatar:hover {
    filter: brightness(0.8);
  }
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.6rem;
}
@media screen and (min-width: 670px) {
  .avatar-fallback {
    font-size: 2rem;
  }
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.avatar-camera-icon-holder {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -6%;
  right: -6%;
  z-index: 10;
  padding: 0.4rem;
  background: #efefef;
  border-radius: 50%;
}

.avatar-camera-icon {
  width: 1.2rem;
  height: 1.2rem;
  transform: translateY(1px);
}

.avatar-fallback-text {
  width: fit-content;
}


.profile-header-stats {
  gap: 1rem;
}

:deep(.v-text-field__prefix) {
  top: 0 !important;
}

.profile-id-field :deep(.v-field--disabled),
.disabled-readable :deep(.v-field--disabled) {
  color: rgba(0, 0, 0, 0.87) !important;
  opacity: 1 !important;
}

.profile-id-field :deep(.v-field--disabled .v-field__input),
.profile-id-field :deep(.v-field--disabled .v-text-field__prefix),
.disabled-readable :deep(.v-field--disabled .v-field__input) {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}

.action-buttons {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  z-index: 100;
  background: #fff;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  border-top: thin solid #dcdcdc;
  padding: 1rem;
  box-shadow: 0px -3px 6px rgba(0,0,0,0.025);
}

.profile-summary-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.profile-summary-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 16px 24px;
}

.profile-summary-dialog-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  align-items: center;
  justify-content: center;
}

.profile-summary-content {
  font-size: 14px;
  color: #444;
  line-height: 1.7;
}

.profile-summary-avatar {
  width: 4.5rem;
  height: 4.5rem;
  flex-shrink: 0;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.2rem;
}

.profile-summary-avatar-img {
  border-radius: 50%;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-summary-avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.5rem;
  color: #666;
}

.profile-summary-name {
  font-size: 20px;
  font-weight: 600;
  color: #222;
}

.profile-summary-line {
  color: #444;
}

.profile-summary-tags {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.profile-summary-links {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.profile-summary-link-line {
  padding: 3px 0;
  color: #444;
}

.profile-summary-link-line a {
  color: #1a73e8;
  text-decoration: none;
}

.profile-summary-link-line a:hover {
  text-decoration: underline;
}

.rm-logo-icon {
  width: 22px;
  height: 22px;
  margin-right: 4px;
}

.profile-summary-tag-line {
  padding: 3px 0;
  color: #444;
}

.spouse-edits-panel {
  border: thin solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 16px 20px;
  background: var(--ds-brand-primary-light, rgba(69, 182, 169, 0.06));
}

.spouse-edits-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
}

.spouse-edits-subtitle {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
  margin-bottom: 12px;
}

.spouse-edit-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: thin solid rgba(0, 0, 0, 0.08);
}

.spouse-edit-changes {
  flex: 1 1 auto;
  min-width: 0;
}

.spouse-edit-change {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  font-size: 14px;
  padding: 1px 0;
}

.spouse-edit-field {
  font-weight: 600;
  color: #333;
}

.spouse-edit-values {
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.6);
  min-width: 0;
}

.spouse-edit-old {
  text-decoration: line-through;
  opacity: 0.7;
}

.spouse-edit-new {
  color: #222;
  font-weight: 500;
}
</style>
